import type { ReactNode } from "react";
import {
  CalendarCheck2,
  GraduationCap,
  Home,
  Languages,
  Map,
  Newspaper,
  Settings,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import type { User } from "../auth";
import { AssistantPageContextProvider, type AssistantPage, type AssistantPageContextValue } from "../assistant-page-context";
import { AiAssistant } from "./AiAssistant";
import { AccountSwitcher } from "./AccountSwitcher";
import { TravelJobIndicator } from "./TravelJobIndicator";
import {
  isMobileNavigationActive,
  type MobileNavigationKey,
} from "../navigation";

const desktopNav = [
  { to: "/", label: "首页", icon: Home },
  { to: "/travel", label: "旅行", icon: Map },
  { to: "/planner", label: "计划", icon: CalendarCheck2 },
  { to: "/words", label: "英语", icon: Sparkles },
  { to: "/french", label: "法语", icon: Languages },
  { to: "/news", label: "资讯", icon: Newspaper },
];

const mobileNav: Array<{
  key: MobileNavigationKey;
  to: string;
  label: string;
  icon: typeof Home;
}> = [
  { key: "home", to: "/", label: "首页", icon: Home },
  {
    key: "learning",
    to: "/learning",
    label: "学习",
    icon: GraduationCap,
  },
  { key: "news", to: "/news", label: "资讯", icon: Newspaper },
  { key: "settings", to: "/settings", label: "设置", icon: Settings },
];

const titles: Record<string, { title: string; subtitle: string }> = {
  "/": {
    title: "今天，也照顾好自己",
    subtitle: "一步一步，把想做的事变成生活",
  },
  "/travel": {
    title: "旅行灵感实验室",
    subtitle: "让路线顺一点，也让旅途松弛一点",
  },
  "/planner": {
    title: "计划与节奏",
    subtitle: "收好想法，看见一周，也给重要的事留出位置",
  },
  "/learning": {
    title: "学习中心",
    subtitle: "选择英语或法语，继续今天的一小步",
  },
  "/words": {
    title: "英语成长路线",
    subtitle: "从日常能力到雅思考试，建立真正会用的英语",
  },
  "/french": {
    title: "法语成长路线",
    subtitle: "从正确发音到真实语境，建立独立法语能力",
  },
  "/ielts": {
    title: "雅思自适应备考",
    subtitle: "从诊断到考场，让每一分都有证据",
  },
  "/news": { title: "今日资讯", subtitle: "看见世界，也保留自己的判断" },
  "/settings": { title: "工作台设置", subtitle: "管理账户、位置与个人偏好" },
  "/admin": { title: "账户管理后台", subtitle: "维护成员账户与登录安全" },
};

export function AppShell({
  user,
  children,
}: {
  user: User;
  children: ReactNode;
}) {
  const location = useLocation();
  const meta = titles[location.pathname] || titles["/"];
  const pageByPath:Record<string,AssistantPage>={"/":"dashboard","/travel":"travel","/planner":"planner","/words":"english","/french":"french","/ielts":"ielts","/news":"news","/settings":"settings"};
  const initial:AssistantPageContextValue={page:pageByPath[location.pathname]||"unknown",route:`${location.pathname}${location.search}`,title:meta.title,view:new URLSearchParams(location.search).get("view")||undefined,instanceId:`${location.pathname}:${location.key}`};
  return (
    <AssistantPageContextProvider key={location.key} initial={initial}><div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">P</span>
          <span>
            <strong>Prive</strong>
            <small>生活工作台</small>
          </span>
        </div>
        <nav>
          {desktopNav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"}>
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-bottom">
          {user.role === "admin" && (
            <NavLink to="/admin">
              <ShieldCheck size={20} />
              <span>管理后台</span>
            </NavLink>
          )}
          <NavLink to="/settings">
            <Settings size={20} />
            <span>设置</span>
          </NavLink>
          <AccountSwitcher />
        </div>
      </aside>
      <main className="main-area">
        <header className="topbar">
          <div>
            <p className="eyebrow">MY GENTLE WORKSPACE</p>
            <h1>{meta.title}</h1>
            <p>{meta.subtitle}</p>
          </div>
          <div className="topbar-tools">
            <div className="topbar-bloom" aria-hidden="true">🌺</div>
            <AccountSwitcher compact />
          </div>
        </header>
        <div className="page-content">{children}</div>
      </main>
      <nav className="mobile-nav" aria-label="主要功能">
        <div className="mobile-nav-left">
          <MobileNavLink item={mobileNav[0]} pathname={location.pathname} />
        </div>
        <MobileNavLink item={mobileNav[1]} pathname={location.pathname} primary />
        <div className="mobile-nav-right">
          <MobileNavLink item={mobileNav[2]} pathname={location.pathname} />
          <MobileNavLink item={mobileNav[3]} pathname={location.pathname} />
        </div>
      </nav>
      <TravelJobIndicator />
      <AiAssistant />
    </div></AssistantPageContextProvider>
  );
}

function MobileNavLink({
  item,
  pathname,
  primary = false,
}: {
  item: (typeof mobileNav)[number];
  pathname: string;
  primary?: boolean;
}) {
  const active = isMobileNavigationActive(item.key, pathname);
  return (
    <Link
      to={item.to}
      className={`${primary ? "mobile-nav-primary " : ""}${active ? "active" : ""}`}
      aria-current={active ? "page" : undefined}
    >
      <item.icon size={primary ? 24 : 20} />
      <span>{item.label}</span>
    </Link>
  );
}
